// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { LayoutDashboard, Upload, Users, FileText, Settings, Zap, BarChart2, LogOut } from 'lucide-react';
import toast from 'react-hot-toast';
import Dashboard from './pages/Dashboard';
import UploadPage from './pages/UploadPage';
import RecipientsPage from './pages/RecipientsPage';
import RunLogPage from './pages/RunLogPage';
import PromptsPage from './pages/PromptsPage';
import AnalyzedReportsPage from './pages/AnalyzedReportsPage';
import LoginPage from './pages/LoginPage';
import { getMe, logout } from './utils/api';
import './App.css';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
});

const NAV = [
  { to: '/',        icon: LayoutDashboard, label: 'Dashboard'        },
  { to: '/upload',  icon: Upload,          label: 'Upload'           },
  { to: '/reports', icon: BarChart2,       label: 'Analyzed Reports' },
  { to: '/recipients', icon: Users,        label: 'Recipients'       },
  { to: '/runs',    icon: FileText,        label: 'Run Log'          },
  { to: '/prompts', icon: Settings,        label: 'Prompts'          },
];

function AppShell({ user, onLogout }) {
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <Zap size={22} className="brand-icon" />
          <span>QB Automate</span>
        </div>

        <nav className="sidebar-nav">
          {NAV.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
            >
              <Icon size={18} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, fontFamily: 'var(--font-mono)' }}>
            {user}
          </div>
          <button
            className="btn btn-secondary btn-sm"
            style={{ width: '100%', justifyContent: 'center' }}
            onClick={onLogout}
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </aside>

      <main className="main-content">
        <Routes>
          <Route path="/"            element={<Dashboard />}          />
          <Route path="/upload"      element={<UploadPage />}         />
          <Route path="/reports"     element={<AnalyzedReportsPage />} />
          <Route path="/recipients"  element={<RecipientsPage />}     />
          <Route path="/runs"        element={<RunLogPage />}         />
          <Route path="/prompts"     element={<PromptsPage />}        />
          <Route path="*"            element={<Navigate to="/" />}    />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  // null = checking, false = not logged in, string = email
  const [user, setUser] = useState(null);

  useEffect(() => {
    getMe()
      .then(data => setUser(data.email))
      .catch(() => setUser(false));
  }, []);

  async function handleLogout() {
    try {
      await logout();
    } catch {}
    setUser(false);
    toast.success('Signed out');
  }

  // Still checking session
  if (user === null) {
    return (
      <div style={{
        minHeight: '100vh', background: 'var(--navy)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <span className="spinner" />
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {user
          ? <AppShell user={user} onLogout={handleLogout} />
          : <LoginPage onLogin={email => setUser(email)} />
        }

        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#1a1a2e',
              color: '#f0f0f0',
              border: '1px solid rgba(226,185,111,0.3)',
              borderRadius: '8px',
              fontSize: '13px',
            },
            success: { iconTheme: { primary: '#e2b96f', secondary: '#1a1a2e' } },
          }}
        />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
